import { useMemo, useState } from 'react';
import {
  Plus,
  RefreshCw,
  Eye,
  Trash2,
  PackageCheck,
  AlertTriangle,
  Search,
  Boxes,
  Package,
  Truck,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Pagination } from '../components/ui/pagination';
import { LoadingTable } from '../components/ui/loading-states';
import { ErrorState } from '../components/ui/error-state';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  useStockRequisitions,
  useStockRequisition,
  useCreateStockRequisition,
  useMarkStockRequisitionReceived,
} from '../hooks/useStockRequisitions';
import { useOneCStock } from '../hooks/useOneCStock';
import { useDepartmentsForSelect } from '../hooks/useDepartments';
import { useWarehousesForSelect } from '../hooks/useWarehouses';
import {
  MedicationPicker,
  aggregateStockOptions,
  type StockOption,
} from '../components/stock/medication-picker';
import { TransfersTab } from '../components/stock/TransfersTab';
import type {
  StockRequisition,
  StockRequisitionStatus,
  CreateStockRequisitionItem,
  MarkReceivedItem,
  MarkReceivedDiscrepancy,
} from '../types/stock-requisitions.types';

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

const STATUS_META: Record<
  StockRequisitionStatus,
  { label: string; variant: BadgeVariant }
> = {
  new: { label: 'Новая', variant: 'secondary' },
  sent_to_1c: { label: 'Отправлена в 1С', variant: 'outline' },
  pending_approval: { label: 'Ожидает одобрения', variant: 'outline' },
  approved: { label: 'Одобрена', variant: 'default' },
  rejected: { label: 'Отклонена', variant: 'destructive' },
  transfer_created: { label: 'Перемещение создано', variant: 'default' },
  in_transit: { label: 'В пути', variant: 'default' },
  completed: { label: 'Получена', variant: 'default' },
  completed_with_discrepancies: {
    label: 'Получена с расхождениями',
    variant: 'destructive',
  },
  cancelled: { label: 'Отменена', variant: 'secondary' },
};

const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, meta]) => ({
  value: value as StockRequisitionStatus,
  label: meta.label,
}));

/** Статусы, в которых заявку можно принять (подтвердить получение). */
const RECEIVABLE_STATUSES: StockRequisitionStatus[] = [
  'approved',
  'transfer_created',
  'in_transit',
];

function StatusBadge({ status }: { status: StockRequisitionStatus }) {
  const meta = STATUS_META[status] ?? { label: status, variant: 'secondary' };
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

function formatDate(value?: string): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('ru-RU');
}

const ALL = '__all__';

export function StockRequisitions() {
  const [tab, setTab] = useState('requisitions');
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      {/* Заголовок */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Заявки</h1>
          <p className="text-muted-foreground">
            Остатки склада, заявки отделений на медикаменты и перемещения.
            Одобрение делает провизор в 1С, статусы синхронизируются
            автоматически.
          </p>
        </div>
        {tab === 'requisitions' && (
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Новая заявка
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="stock">
            <Boxes className="w-4 h-4 mr-2" />
            Остатки
          </TabsTrigger>
          <TabsTrigger value="requisitions">
            <Package className="w-4 h-4 mr-2" />
            Заявки
          </TabsTrigger>
          <TabsTrigger value="transfers">
            <Truck className="w-4 h-4 mr-2" />
            Перемещения
          </TabsTrigger>
        </TabsList>

        <TabsContent value="stock">
          <StockTab />
        </TabsContent>

        <TabsContent value="requisitions">
          <RequisitionsTab onOpenDetail={setDetailId} />
        </TabsContent>

        <TabsContent value="transfers">
          <TransfersTab />
        </TabsContent>
      </Tabs>

      {showCreate && (
        <CreateRequisitionDialog onClose={() => setShowCreate(false)} />
      )}
      <RequisitionDetailDialog
        id={detailId}
        onClose={() => setDetailId(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Вкладка «Заявки» — список с фильтрами и пагинацией
// ---------------------------------------------------------------------------

function RequisitionsTab({
  onOpenDetail,
}: {
  onOpenDetail: (id: string) => void;
}) {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>(ALL);
  const [departmentFilter, setDepartmentFilter] = useState<string>(ALL);

  const filters = useMemo(
    () => ({
      page,
      limit: 20,
      status:
        statusFilter !== ALL
          ? (statusFilter as StockRequisitionStatus)
          : undefined,
      departmentId: departmentFilter !== ALL ? departmentFilter : undefined,
    }),
    [page, statusFilter, departmentFilter],
  );

  const { data, isLoading, error, refetch, isFetching } =
    useStockRequisitions(filters);
  const { data: departments } = useDepartmentsForSelect();

  const rows = data?.data ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Список заявок</CardTitle>
            <CardDescription>
              Фильтр по статусу и отделению. Нажмите на строку, чтобы открыть
              детали.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`}
            />
            Обновить
          </Button>
        </div>
        <div className="flex flex-wrap gap-3 mt-2">
          <div className="w-56">
            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Статус" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Все статусы</SelectItem>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-56">
            <Select
              value={departmentFilter}
              onValueChange={(v) => {
                setDepartmentFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Отделение" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Все отделения</SelectItem>
                {(departments ?? []).map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingTable rows={6} columns={6} />
        ) : error ? (
          <ErrorState
            title="Не удалось загрузить заявки"
            description={(error as Error)?.message}
            onAction={() => refetch()}
          />
        ) : rows.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            Заявок пока нет
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Отделение</TableHead>
                  <TableHead>Склад</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead>Срочность</TableHead>
                  <TableHead className="text-right">Позиций</TableHead>
                  <TableHead>Создана</TableHead>
                  <TableHead>Док. 1С</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer"
                    onClick={() => onOpenDetail(row.id)}
                  >
                    <TableCell className="font-medium">
                      {row.requestingDepartmentName ||
                        row.requestingDepartmentId.slice(0, 8)}
                    </TableCell>
                    <TableCell>
                      {row.warehouseName || row.warehouseId.slice(0, 8)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={row.status} />
                    </TableCell>
                    <TableCell>
                      {row.urgency === 'urgent' ? (
                        <Badge variant="destructive">Срочно</Badge>
                      ) : (
                        <span className="text-muted-foreground">Обычная</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {row.items?.length ?? 0}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(row.createdAt)}
                    </TableCell>
                    <TableCell className="text-xs font-mono">
                      {row.onecDocId || '—'}
                    </TableCell>
                    <TableCell>
                      <Eye className="w-4 h-4 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="mt-4">
              <Pagination
                currentPage={data?.page ?? 1}
                totalPages={data?.totalPages ?? 1}
                onPageChange={setPage}
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Вкладка «Остатки» — остатки маркированных медикаментов склада
// ---------------------------------------------------------------------------

function StockTab() {
  const [warehouseId, setWarehouseId] = useState<string>(ALL);
  const [search, setSearch] = useState('');

  const { data: warehouses } = useWarehousesForSelect();
  const { data, isLoading, error, refetch, isFetching } = useOneCStock(
    warehouseId !== ALL ? warehouseId : undefined,
  );

  const rows = useMemo(() => data?.items ?? [], [data?.items]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.medication || '').toLowerCase().includes(q) ||
        (r.medicationCode || '').toLowerCase().includes(q) ||
        (r.batch || '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  const fetchedAtLabel = data?.fetchedAt
    ? new Date(data.fetchedAt).toLocaleString('ru-RU')
    : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Остатки склада</CardTitle>
            <CardDescription>
              Актуальные остатки медикаментов из 1С.
              {data?.warehouseName ? ` Склад: ${data.warehouseName}.` : ''}
              {fetchedAtLabel ? ` Обновлено: ${fetchedAtLabel}.` : ''}
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`}
            />
            Обновить
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-2">
          <div className="w-64">
            <Select value={warehouseId} onValueChange={setWarehouseId}>
              <SelectTrigger>
                <SelectValue placeholder="Склад" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Мой склад (по умолчанию)</SelectItem>
                {(warehouses ?? []).map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Поиск по названию, коду или партии"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingTable rows={6} columns={5} />
        ) : error ? (
          <ErrorState
            title="Не удалось загрузить остатки"
            description={
              (error as Error)?.message ||
              'Проверьте, что интеграция с 1С настроена, а склад имеет код 1С.'
            }
            onAction={() => refetch()}
          />
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            {rows.length === 0
              ? 'Нет товаров на остатках'
              : 'Ничего не найдено по запросу'}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Наименование</TableHead>
                <TableHead>Код 1С</TableHead>
                <TableHead>Партия</TableHead>
                <TableHead>Склад</TableHead>
                <TableHead className="text-right">Количество</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((row, idx) => (
                <TableRow key={`${row.medicationCode}-${row.batch}-${idx}`}>
                  <TableCell>{row.medication}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {row.medicationCode || '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {row.batch || '—'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {row.warehouse || '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant="secondary">{row.quantity}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Диалог создания заявки
// ---------------------------------------------------------------------------

interface ItemRow {
  medicationName: string;
  medicationCode1C: string;
  requestedQuantity: string;
  uom: string;
  /** Доступный остаток выбранного медикамента на складе-отправителе (0 — не выбран). */
  available: number;
}

const emptyRow = (): ItemRow => ({
  medicationName: '',
  medicationCode1C: '',
  requestedQuantity: '',
  uom: 'упаковка',
  available: 0,
});

function CreateRequisitionDialog({ onClose }: { onClose: () => void }) {
  const createMutation = useCreateStockRequisition();
  const { data: departments } = useDepartmentsForSelect();
  const { data: warehouses } = useWarehousesForSelect();

  const [departmentId, setDepartmentId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [recipientWarehouseId, setRecipientWarehouseId] = useState('');
  const [urgency, setUrgency] = useState('normal');
  const [rows, setRows] = useState<ItemRow[]>([emptyRow()]);

  // Остатки выбранного склада-ОТПРАВИТЕЛЯ из 1С — единственный источник позиций.
  const {
    data: stock,
    isLoading: stockLoading,
    isError: stockError,
  } = useOneCStock(warehouseId || undefined);

  // Агрегируем остатки по коду номенклатуры: суммируем количество по всем партиям.
  const stockOptions = useMemo<StockOption[]>(
    () => aggregateStockOptions(stock?.items),
    [stock],
  );

  const updateRow = (idx: number, patch: Partial<ItemRow>) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (idx: number) =>
    setRows((prev) => prev.filter((_, i) => i !== idx));

  // Смена склада-отправителя обнуляет позиции — прежние остатки уже неактуальны.
  const handleWarehouseChange = (id: string) => {
    setWarehouseId(id);
    setRows([emptyRow()]);
  };

  // Выбор медикамента только из остатков склада — код и лимит подтягиваются автоматически.
  const selectMedication = (idx: number, opt: StockOption) =>
    updateRow(idx, {
      medicationName: opt.name,
      medicationCode1C: opt.code,
      available: opt.available,
    });

  // Уже выбранные в других строках коды — чтобы не выбрать один медикамент дважды.
  const chosenCodes = useMemo(
    () => rows.map((r) => r.medicationCode1C).filter(Boolean),
    [rows],
  );

  const rowError = (r: ItemRow): string | null => {
    if (!r.medicationName || !r.requestedQuantity) return null;
    const qty = Number(r.requestedQuantity);
    if (!Number.isFinite(qty) || qty <= 0) return 'Укажите количество';
    if (qty > r.available) return `Больше остатка (доступно ${r.available})`;
    return null;
  };
  const hasRowErrors = rows.some((r) => rowError(r) !== null);

  const validItems: CreateStockRequisitionItem[] = rows
    .filter((r) => {
      const qty = Number(r.requestedQuantity);
      return (
        !!r.medicationName &&
        r.available > 0 &&
        Number.isFinite(qty) &&
        qty > 0 &&
        qty <= r.available
      );
    })
    .map((r, i) => ({
      lineNumber: i + 1,
      medicationCode1C: r.medicationCode1C.trim() || undefined,
      medicationName: r.medicationName.trim(),
      requestedQuantity: Number(r.requestedQuantity),
      uom: r.uom.trim() || undefined,
    }));

  const canSubmit =
    !!departmentId &&
    !!warehouseId &&
    !!recipientWarehouseId &&
    validItems.length > 0 &&
    !hasRowErrors;

  const handleCreate = async () => {
    if (!canSubmit) return;
    await createMutation.mutateAsync({
      requestingDepartmentId: departmentId,
      warehouseId,
      recipientWarehouseId,
      urgency,
      items: validItems,
    });
    onClose();
  };

  const stockReady =
    !!warehouseId && !stockLoading && !stockError && stockOptions.length > 0;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Новая заявка на остатки</DialogTitle>
          <DialogDescription>
            Заявка уйдёт в 1С на одобрение провизору. Позиции подбираются из
            остатков склада-отправителя.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Отделение-заявитель</Label>
            <Select value={departmentId} onValueChange={setDepartmentId}>
              <SelectTrigger>
                <SelectValue placeholder="Выберите отделение" />
              </SelectTrigger>
              <SelectContent>
                {(departments ?? []).map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Склад-отправитель (провизора)</Label>
            <Select value={warehouseId} onValueChange={handleWarehouseChange}>
              <SelectTrigger>
                <SelectValue placeholder="Выберите склад" />
              </SelectTrigger>
              <SelectContent>
                {(warehouses ?? []).map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Склад-получатель</Label>
            <Select
              value={recipientWarehouseId}
              onValueChange={setRecipientWarehouseId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Выберите склад" />
              </SelectTrigger>
              <SelectContent>
                {(warehouses ?? []).map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Срочность</Label>
            <Select value={urgency} onValueChange={setUrgency}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Обычная</SelectItem>
                <SelectItem value="urgent">Срочная</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Позиции */}
        <div className="mt-2">
          <div className="flex items-center justify-between mb-2">
            <Label>Позиции</Label>
            <Button
              variant="outline"
              size="sm"
              onClick={addRow}
              disabled={!stockReady}
            >
              <Plus className="w-4 h-4 mr-1" />
              Добавить
            </Button>
          </div>

          {!warehouseId ? (
            <p className="text-sm text-muted-foreground py-4 text-center border rounded-md">
              Сначала выберите склад-отправитель — позиции подбираются из его
              остатков в 1С.
            </p>
          ) : stockLoading ? (
            <p className="text-sm text-muted-foreground py-4 text-center border rounded-md">
              Загрузка остатков склада…
            </p>
          ) : stockError ? (
            <p className="text-sm text-destructive py-4 text-center border rounded-md">
              Не удалось загрузить остатки склада. Проверьте настройку интеграции
              с 1С и код 1С у склада.
            </p>
          ) : stockOptions.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center border rounded-md">
              На выбранном складе нет остатков.
            </p>
          ) : (
            <div className="min-h-[420px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Медикамент (из остатков склада)</TableHead>
                  <TableHead className="w-28 text-right">Кол-во</TableHead>
                  <TableHead className="w-28">Ед.</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, idx) => {
                  const err = rowError(r);
                  const rowOptions = stockOptions.filter(
                    (o) =>
                      o.code === r.medicationCode1C ||
                      !chosenCodes.includes(o.code),
                  );
                  return (
                    <TableRow key={idx}>
                      <TableCell className="align-top">
                        <MedicationPicker
                          options={rowOptions}
                          selectedName={r.medicationName}
                          onSelect={(opt) => selectMedication(idx, opt)}
                        />
                        {r.available > 0 && (
                          <span className="text-xs text-muted-foreground">
                            Доступно: {r.available}
                            {r.medicationCode1C
                              ? ` · код 1С: ${r.medicationCode1C}`
                              : ''}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="align-top">
                        <Input
                          type="number"
                          min={1}
                          max={r.available || undefined}
                          value={r.requestedQuantity}
                          disabled={r.available === 0}
                          onChange={(e) =>
                            updateRow(idx, {
                              requestedQuantity: e.target.value,
                            })
                          }
                          className={`text-right ${err ? 'border-destructive' : ''}`}
                        />
                        {err && (
                          <span className="text-xs text-destructive">
                            {err}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="align-top">
                        <Input
                          value={r.uom}
                          onChange={(e) =>
                            updateRow(idx, { uom: e.target.value })
                          }
                        />
                      </TableCell>
                      <TableCell className="align-top">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeRow(idx)}
                          disabled={rows.length === 1}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          )}
        </div>

        {createMutation.isError && (
          <p className="text-sm text-destructive">
            {(createMutation.error as Error)?.message ||
              'Не удалось создать заявку'}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!canSubmit || createMutation.isPending}
          >
            {createMutation.isPending ? 'Создание…' : 'Создать заявку'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Диалог деталей заявки + приёмка
// ---------------------------------------------------------------------------

function RequisitionDetailDialog({
  id,
  onClose,
}: {
  id: string | null;
  onClose: () => void;
}) {
  const { data, isLoading, error, refetch } = useStockRequisition(id);

  return (
    <Dialog open={!!id} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Заявка на остатки</DialogTitle>
          {data && (
            <DialogDescription>
              {data.requestingDepartmentName || data.requestingDepartmentId} ·{' '}
              {data.warehouseName || data.warehouseId}
              {data.recipientWarehouseName
                ? ` → ${data.recipientWarehouseName}`
                : ''}
            </DialogDescription>
          )}
        </DialogHeader>

        {isLoading ? (
          <LoadingTable rows={5} columns={4} />
        ) : error ? (
          <ErrorState
            title="Не удалось загрузить заявку"
            description={(error as Error)?.message}
            onAction={() => refetch()}
          />
        ) : data ? (
          <RequisitionDetailBody requisition={data} onClose={onClose} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function RequisitionDetailBody({
  requisition,
  onClose,
}: {
  requisition: StockRequisition;
  onClose: () => void;
}) {
  const markReceived = useMarkStockRequisitionReceived();
  const canReceive = RECEIVABLE_STATUSES.includes(requisition.status);
  const [receiving, setReceiving] = useState(false);

  return (
    <div className="space-y-5">
      {/* Сводка */}
      <div className="flex flex-wrap items-center gap-3">
        <StatusBadge status={requisition.status} />
        {requisition.urgency === 'urgent' && (
          <Badge variant="destructive">Срочно</Badge>
        )}
        <span className="text-sm text-muted-foreground">
          Создана: {formatDate(requisition.createdAt)}
          {requisition.createdByEmployeeName
            ? ` · ${requisition.createdByEmployeeName}`
            : ''}
        </span>
      </div>

      {requisition.reason && (
        <p className="text-sm">
          <span className="text-muted-foreground">Причина: </span>
          {requisition.reason}
        </p>
      )}

      {/* Позиции */}
      <div>
        <h3 className="text-sm font-medium mb-2">Позиции</h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>GTIN</TableHead>
              <TableHead>Наименование</TableHead>
              <TableHead className="text-right">Запрошено</TableHead>
              <TableHead className="text-right">Одобрено</TableHead>
              <TableHead className="text-right">Получено</TableHead>
              <TableHead>Ед.</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {requisition.items.map((it) => (
              <TableRow key={it.id || it.lineNumber}>
                <TableCell className="font-mono text-xs">
                  {it.medicationGtin}
                </TableCell>
                <TableCell>{it.medicationName || '—'}</TableCell>
                <TableCell className="text-right">
                  {it.requestedQuantity}
                </TableCell>
                <TableCell className="text-right">
                  {it.approvedQuantity ?? '—'}
                </TableCell>
                <TableCell className="text-right">
                  {it.receivedQuantity ?? '—'}
                </TableCell>
                <TableCell>{it.uom || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Расхождения */}
      {requisition.discrepancies && requisition.discrepancies.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-2 flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4 text-destructive" />
            Расхождения
          </h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Поз.</TableHead>
                <TableHead>Тип</TableHead>
                <TableHead className="text-right">Ожидалось</TableHead>
                <TableHead className="text-right">Факт</TableHead>
                <TableHead>Причина</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requisition.discrepancies.map((d) => (
                <TableRow key={d.id || `${d.lineNumber}-${d.type}`}>
                  <TableCell>{d.lineNumber}</TableCell>
                  <TableCell>{d.type}</TableCell>
                  <TableCell className="text-right">
                    {d.expectedQuantity ?? '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    {d.actualQuantity ?? '—'}
                  </TableCell>
                  <TableCell>{d.reason || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Информация 1С */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Интеграция с 1С</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
          <InfoLine label="Статус синхр." value={requisition.onecSyncStatus} />
          <InfoLine label="Документ 1С" value={requisition.onecDocId} mono />
          <InfoLine
            label="Перемещение"
            value={requisition.onecTransferId}
            mono
          />
          <InfoLine
            label="Одобрена"
            value={
              requisition.approvedAt ? formatDate(requisition.approvedAt) : null
            }
          />
          {requisition.onecSyncError && (
            <div className="col-span-2 text-destructive">
              Ошибка 1С: {requisition.onecSyncError}
            </div>
          )}
          {requisition.rejectionReason && (
            <div className="col-span-2 text-destructive">
              Причина отклонения: {requisition.rejectionReason}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Приёмка */}
      {canReceive &&
        (receiving ? (
          <ReceiveForm
            requisition={requisition}
            pending={markReceived.isPending}
            errorMessage={
              markReceived.isError
                ? (markReceived.error as Error)?.message
                : undefined
            }
            onCancel={() => setReceiving(false)}
            onSubmit={async (payload) => {
              await markReceived.mutateAsync({
                id: requisition.id,
                data: payload,
              });
              onClose();
            }}
          />
        ) : (
          <Button onClick={() => setReceiving(true)}>
            <PackageCheck className="w-4 h-4 mr-2" />
            Подтвердить получение
          </Button>
        ))}
    </div>
  );
}

function InfoLine({
  label,
  value,
  mono,
}: {
  label: string;
  value?: string | null;
  mono?: boolean;
}) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? 'font-mono text-xs' : ''}>{value || '—'}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Форма приёмки (подтверждение получения)
// ---------------------------------------------------------------------------

function ReceiveForm({
  requisition,
  pending,
  errorMessage,
  onCancel,
  onSubmit,
}: {
  requisition: StockRequisition;
  pending: boolean;
  errorMessage?: string;
  onCancel: () => void;
  onSubmit: (payload: {
    receivedItems: MarkReceivedItem[];
    discrepancies?: MarkReceivedDiscrepancy[];
  }) => void;
}) {
  // По умолчанию принимаем сколько одобрено (или запрошено).
  const [qty, setQty] = useState<Record<number, string>>(() =>
    Object.fromEntries(
      requisition.items.map((it) => [
        it.lineNumber,
        String(it.approvedQuantity ?? it.requestedQuantity),
      ]),
    ),
  );

  const handleSubmit = () => {
    const receivedItems: MarkReceivedItem[] = requisition.items.map((it) => ({
      lineNumber: it.lineNumber,
      receivedQuantity: Number(qty[it.lineNumber] ?? 0) || 0,
    }));

    // Автоматически выявляем расхождения (получено < ожидалось).
    const discrepancies: MarkReceivedDiscrepancy[] = requisition.items
      .map((it) => {
        const expected = it.approvedQuantity ?? it.requestedQuantity;
        const actual = Number(qty[it.lineNumber] ?? 0) || 0;
        if (actual < expected) {
          return {
            lineNumber: it.lineNumber,
            type: 'shortfall',
            expectedQuantity: expected,
            actualQuantity: actual,
            differenceQuantity: actual - expected,
            reason: 'Недопоставка при приёмке',
          } as MarkReceivedDiscrepancy;
        }
        return null;
      })
      .filter((d): d is MarkReceivedDiscrepancy => d !== null);

    onSubmit({
      receivedItems,
      discrepancies: discrepancies.length ? discrepancies : undefined,
    });
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Подтверждение получения</CardTitle>
        <CardDescription>
          Укажите фактически полученное количество. Недопоставки будут
          зафиксированы как расхождения автоматически.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Наименование</TableHead>
              <TableHead className="text-right">Ожидается</TableHead>
              <TableHead className="w-32 text-right">Получено</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {requisition.items.map((it) => {
              const expected = it.approvedQuantity ?? it.requestedQuantity;
              return (
                <TableRow key={it.id || it.lineNumber}>
                  <TableCell>
                    {it.medicationName || it.medicationGtin}
                  </TableCell>
                  <TableCell className="text-right">{expected}</TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min={0}
                      value={qty[it.lineNumber] ?? ''}
                      onChange={(e) =>
                        setQty((prev) => ({
                          ...prev,
                          [it.lineNumber]: e.target.value,
                        }))
                      }
                      className="text-right"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {errorMessage && (
          <p className="text-sm text-destructive">{errorMessage}</p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={pending}>
            Отмена
          </Button>
          <Button onClick={handleSubmit} disabled={pending}>
            {pending ? 'Сохранение…' : 'Подтвердить'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
